import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate, useSearchParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppForm from '../../components/form/form-client/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import FormFileThumbnail from 'src/components/form/general-input-form/FormFileThumbnail'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import { singleCountriesOptionsSelect } from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'

const AddClient = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [searchParams] = useSearchParams();

  const [countryOptions, setCountryOptions] = useState('')
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subDistrictOptions, setSubDistrictOptions] = useState([]);
  const [countryPhone, setCountryPhone] = useState(null)
  const [openFilePreview, setOpenFilePreview] = useState(false)

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })
  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {} })
  const [showConfirmAddAttachments, setShowConfirmAddAttachments] = useState({ show: false, item: {} })
  
  const [countryValue, setCountryValue] = useState(null)
  const [provincedValue, setProvincedValue] = useState(null)
  const [cityValue, setCityValue] = useState(null)
  const [districtValue, setDistrictValue] = useState(null)
  const [subDistrictValue, setSubDistrictValue] = useState(null)

  const [defaultCountryIdIndonesia, setDefaultCountryIdIndonesia] = useState('104')

  const formik = useFormik({
    initialValues: {
      client_name: '',
      client_description: '',
      client_address: '',
      pic_name: '',
      phone_number: '',
      pic_country_id: '',
      pic_job_position: '',
      pic_email_address: '',
      pic_department: '',
      pic_additional_notes: '',
      client_country_id: '',
      client_province_id: '',
      client_city_id: '',
      client_district_id: '',
      client_subdistrict_id: '',
      client_postal_code: '',
      npwp_image: [],
      others_pic: [],
      attachments: [],
      additional_address: []
    },
    validationSchema: Yup.object().shape({
      client_name: Yup.string()
        .required("Please enter client name."),
      client_description: Yup.string()
        .required("Please enter client description."),
      client_address: Yup.string()
        .required("Please enter client address."),
      pic_name: Yup.string()
        .required("Please enter main point of contact name."),
      phone_number: Yup.string()
        .required("Please enter main point of contact contact number."),
      pic_country_id: Yup.string().required("Please enter main point of contact contact number country."),
      client_country_id: Yup.string().required("Please select client country."),
      client_province_id: Yup.string()
      .when('client_country_id', {
        is: (client_country_id) => client_country_id && client_country_id == defaultCountryIdIndonesia,
        then: () => Yup.string().required('Please select client province.')
      }),
      client_city_id: Yup.string()
      .when('client_country_id', {
        is: (client_country_id) => client_country_id && client_country_id == defaultCountryIdIndonesia,
        then: () => Yup.string().required('Please select client city.')
      }),
      client_district_id: Yup.string()
      .when('client_country_id', {
        is: (client_country_id) => client_country_id && client_country_id == defaultCountryIdIndonesia,
        then: () => Yup.string().required('Please select client district.')
      }),
      client_subdistrict_id: Yup.string()
      .when('client_country_id', {
        is: (client_country_id) => client_country_id && client_country_id == defaultCountryIdIndonesia,
        then: () => Yup.string().required('Please select client sub district.')
      }),
      client_postal_code: Yup.string()
      .when('client_country_id', {
        is: (client_country_id) => client_country_id && client_country_id == defaultCountryIdIndonesia,
        then: () => Yup.string().required('Please enter client postal code.')
      }),
      others_pic: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Please enter other point of contact name."),
          phone_number: Yup.string().required("Please enter other point of contact contact number."),
          country_value: Yup.string().required("Please select country of point of contact."),
          position: Yup.string().required("Please enter point of contact position."),
          email: Yup.string().required("Please enter point of contact email."),
        })
      ),
      pic_job_position: Yup.string()
        .required("Please enter main point of contact job position."),
      pic_email_address: Yup.string()
        .required("Please enter main point of contact email address."),
      additional_address: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Please enter address name."),
          address: Yup.string().required("Please enter address."),
          country_id: Yup.string().required("Please select country."),
          country_value: Yup.object().nullable().required("Please select country."),
          province_id: Yup.string().when('country_value', {
            is: (country_value) => country_value && country_value?.label?.includes("Indonesia"),
            then: () => Yup.string().required('Please select province.')
          }),
          city_id: Yup.string().when('country_value', {
            is: (country_value) => country_value && country_value?.label?.includes("Indonesia"),
            then: () => Yup.string().required('Please select city.')
          }),
          district_id: Yup.string().when('country_value', {
            is: (country_value) => country_value && country_value?.label?.includes("Indonesia"),
            then: () => Yup.string().required('Please select district.')
          }),
          subdistrict_id: Yup.string().when('country_value', {
            is: (country_value) => country_value && country_value?.label?.includes("Indonesia"),
            then: () => Yup.string().required('Please select sub district.')
          }),
          postal_code: Yup.string().when('country_value', {
            is: (country_value) => country_value && country_value?.label?.includes("Indonesia"),
            then: () => Yup.string().required('Please enter postal code.')
          }),
        })
      ),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllCountries()
    fetchAllProvince()

    const clientNameQuery = searchParams.get("name");

    if (clientNameQuery) {
      formik.setValues((values) => ({
      ...values,
      client_name: clientNameQuery
    }), false)
    }
  }, []);

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        setCountryOptions(singleCountriesOptionsSelect(data))
        
        if (data?.length > 0) {
          let countryIdIndo = data?.filter((country) => country?.name?.includes('Indonesia'))?.map((val) => val?.id)
          setDefaultCountryIdIndonesia(countryIdIndo)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const fetchAllProvince = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries/provinces",
    })
      .then(({ data }) => {
        if (data) {
          let finalProvinceOptions = data;
          finalProvinceOptions = finalProvinceOptions.map((prov) => {
            let objProv = {
              label: prov.province_name,
              value: prov.province_id,
            };
            return objProv;
          });
          setProvinceOptions(finalProvinceOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllCities = async (provinceId) => {
    let cities = await getCities(provinceId)
    setCityOptions(cities)
  }

  const fetchAllDistrict = async (cityId) => {
    let districts = await getDistricts(cityId)
    setDistrictOptions(districts);
  }

  const fetchAllSubDistrict = async (district_id) => {
    let subDistricts = await getSubDistricts(district_id)
    setSubDistrictOptions(subDistricts);
  }

  const getCities = async (provinceId) => {
    try {
      const { data } = await Axios.get(
        `/api/tracker/v1/countries/cities/${provinceId}`
      );

      return data.map((city) => ({
        label: city.city_name,
        value: city.city_id,
      }));
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message;
      showError(errMsg);
      throw err;
    }
  };

  const getDistricts = async (cityId) => {
    try {
      const { data } = await Axios.get(
        `/api/tracker/v1/countries/district/${cityId}`
      );

      return data.map((district) => ({
        label: district.district_name,
        value: district.district_id,
      }));
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message;
      showError(errMsg);
      throw err;
    }
  };

  const getSubDistricts = async (districtId) => {
    try {
      const { data } = await Axios.get(
        `/api/tracker/v1/countries/subdistrict/${districtId}`
      );
      
      return data.map((subDistrict) => ({
        label: subDistrict.subdistrict_name,
        value: subDistrict.subdistrict_id,
        postal_code: subDistrict.postal_code
      }));
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message;
      showError(errMsg);
      throw err;
    }
  };

  const handleRemoveImage = (index = 0) => {
    let type = showDeleteItem?.type || 'npwp_image'
    formik.setValues((values) => ({
      ...values,
      [type]: values[type].filter((_, i) => i !== index)
    }), false)

    setShowDeleteItem({ show: false, item: {} })
  }

  const handleImagesOnDrop = (files) => {
    const attachments = []
    if (files[0] && typeof files[0] !== 'undefined') {
      attachments.push(files[0])
      formik.setValues((values) => ({
        ...values,
        npwp_image: attachments
      }), false)
    }
  }

  const handleImageAttachment = (e) => {
    e.preventDefault()
    const files = e.target.files
    const attachments = []
    if (files[0] && typeof files[0] !== 'undefined') {
      attachments.push(files[0])
      formik.setValues((values) => ({
        ...values,
        npwp_image: attachments
      }), false)
    }
  }

  const handleInputValue = (values) => {
    const formData = new FormData()

    formData.append('name', values.client_name)
    formData.append('description', values.client_description)
    formData.append('address', values.client_address)
    formData.append('pic_name', values.pic_name)
    formData.append('pic_contact_number', values.phone_number)
    formData.append('pic_country_id', values.pic_country_id)
    formData.append('pic_job_position', values.pic_job_position)
    formData.append('pic_email_address', values.pic_email_address)
    formData.append('pic_department', values.pic_department)
    formData.append('pic_additional_notes', values.pic_additional_notes)
    formData.append('country_id', values.client_country_id)
    if (countryValue && countryValue?.label?.includes('Indonesia') && values?.client_country_id) {
      formData.append('province_id', values.client_province_id)
      formData.append('city_id', values.client_city_id)
      formData.append('district_id', values.client_district_id)
      formData.append('subdistrict_id', values.client_subdistrict_id)
      formData.append('postal_code', values.client_postal_code)
    }
    if (values.npwp_image.length > 0) {
      formData.append('file', values.npwp_image[0])
    }
    if (values.others_pic.length > 0) {
      formData.append('others_pic', JSON.stringify(values.others_pic))
    }
    if (values?.attachments?.length > 0) {
      for (let i = 0; i < values.attachments.length; i++) {
        formData.append('other_file', values.attachments[i])
      }
    }
    if (values.additional_address.length > 0) {
      formData.append('additional_address', JSON.stringify(values.additional_address))
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/client/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('Client has been successfully created')
          navigate('/client/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleCardHeaderButton = () => {
    setShowConfirmAddAttachments({ show: true, item: {} })
  }

  const handleFileInput = (values, type) => {
    const files = values
    if (files) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        [type]: [...prevValues[type], ...files]
      }), false)
    }

    setShowConfirmAddAttachments({ show: false, item: {} })
  }

  const handleSelectCountryPhone = (data) => {
    formik.setValues((values) => ({
      ...values,
      pic_country_id: data ? JSON.parse(data?.value).id : ''
    }), false)

    setCountryPhone(data? data?.value : null)
  }

  const handleSelectCountry = (data) => {
    if (data?.value) {
      formik.setValues((values) => ({
        ...values,
        client_country_id: data ? JSON.parse(data?.value).id : ''
      }), false)

      setCountryValue({
        label: data?.label,
        value: JSON.parse(data?.value)?.id
      })

      if (!JSON.parse(data?.value)?.name?.includes('Indonesia')) {
        setProvincedValue(null)
        setCityValue(null)
        setDistrictValue(null)
        setSubDistrictValue(null)
      }
    } else {
      setCountryValue(null)
      setProvincedValue(null)
      setCityValue(null)
      setDistrictValue(null)
      setSubDistrictValue(null)

      formik.setValues((values) => ({
        ...values,
        client_country_id: '',
        client_province_id: '',
        client_city_id: '',
        client_district_id: '',
        client_subdistrict_id: '',
        client_postal_code: ''
      }), false)
    }
  }

  const handleSelectProvince = async (data) => {
    if (data?.value) {
      formik.setValues((values) => ({
        ...values,
        client_province_id: data?.value
      }), false)

      if (data?.value != formik?.values?.client_province_id) {
        setCityValue(null)
        setDistrictValue(null)
        setDistrictOptions([])
        setSubDistrictValue(null)
        setSubDistrictOptions([])
      }

      await fetchAllCities(data?.value)
      setProvincedValue(data)
    } else {
      setProvincedValue(null)
      setCityOptions([])
      setCityValue(null)
      setDistrictOptions([])
      setDistrictValue(null)
      setSubDistrictOptions([])
      setSubDistrictValue(null)
    }
  }

  const handleSelectCity = async (data) => {
    if (data?.value) {
      formik.setValues((values) => ({
        ...values,
        client_city_id: data?.value
      }), false)

      if (data?.value != formik?.values?.client_city_id) {
        setDistrictValue(null)
        setDistrictOptions([])
        setSubDistrictValue(null)
        setSubDistrictOptions([])
      }

      await fetchAllDistrict(data?.value)
      setCityValue(data)
    } else {
      setCityValue(null)
      setDistrictOptions([])
      setDistrictValue(null)
      setSubDistrictOptions([])
      setSubDistrictValue(null)
    }
  }

  const handleSelectDistrict = async (data) => {
    if (data?.value) {
      formik.setValues((values) => ({
        ...values,
        client_district_id: data?.value
      }), false)

      if (data?.value != formik?.values?.client_district_id) {
        setSubDistrictValue(null)
        setSubDistrictOptions([])
      }

      await fetchAllSubDistrict(data?.value)
      setDistrictValue(data)
    } else {
      setDistrictValue(null)
      setSubDistrictOptions([])
      setSubDistrictValue(null)
    }
  }

  const handleSelectSubDistrict = (data) => {
    if (data?.value) {
      formik.setValues((values) => ({
        ...values,
        client_subdistrict_id: data?.value,
        client_postal_code: data?.postal_code
      }), false)

      setSubDistrictValue(data)
    } else {
      setSubDistrictValue(null)
    }
  }

  const handleAddOthersPIC = () => {
    formik.setValues((values) => ({
      ...values,
      others_pic: [
        ...values.others_pic,
        ...[
          {
            name: null,
            phone_number: '',
            country_id: '',
            country_value: null,
            position: '',
            email: '',
            department: '',
            additional_notes: ''
          }
        ]
      ]
    }), false)
  }

  const handleChangeOthersPIC = (e, informationIndex) => {
    const name = e?.target?.name
    const value = e?.target?.value

    formik.setValues((values) => {
      const newArrayInformation = values.others_pic.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: value
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        others_pic: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleSelectCountryOthersPIC = (data, informationIndex) => {
    let edataCountryValue = {
      target: {
        name: 'country_value',
        value: data?.value
      }
    }
    handleChangeOthersPIC(edataCountryValue, informationIndex)
  }

  const handleRemoveOthersPIC = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      others_pic: values.others_pic.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }

  const handleAddAdditionalAddress = () => {
    formik.setValues((values) => ({
      ...values,
      additional_address: [
        ...values.additional_address,
        ...[
          {
            name: '',
            address: '',
            country_id: '',
            country_value: null,
            province_id: '',
            province_value: null,
            city_id: '',
            city_value: null,
            district_id: '',
            district_value: null,
            subdistrict_id: '',
            subdistrict_value: null,
            postal_code: '',
          }
        ]
      ]
    }), false)
  }

  const handleChangeAdditionalAddress = (e, informationIndex) => {
    const name = e?.target?.name
    const value = e?.target?.value

    formik.setValues((values) => {
      const newArrayInformation = values.additional_address.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: value
          };
        } else {
          return item;
        }
      });
      
      return {
        ...values,
        additional_address: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleChangeAreaAdditionalAddress = (updates, informationIndex) => {
    formik.setValues((values) => {
      const newArrayInformation = values.additional_address.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            ...updates
          };
        } else {
          return item;
        }
      });
      
      return {
        ...values,
        additional_address: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleSelectAreaAdditionalAddress = async (data, informationIndex, name) => {
    switch (name) {
      case 'country':
        let updates = {
          country_value: data,
          country_id: data ? JSON.parse(data?.value).id : ''
        };

        if (data?.value != formik?.values?.additional_address[informationIndex]?.country_id) {
          updates = {
            ...updates,
            province_id: '',
            province_value: null,
            city_id: '',
            city_value: null,
            city_options: [],
            district_id: '',
            district_value: null,
            district_options: [],
            subdistrict_id: '',
            subdistrict_value: null,
            subdistrict_options: [],
            postal_code: ''
          };
        }
    
        handleChangeAreaAdditionalAddress(updates, informationIndex)
        break;
      case 'province':
        let updatesProvince = {
          province_value: data,
          province_id: data ? data?.value : '',
        };

        if (data?.value != formik?.values?.additional_address[informationIndex]?.province_id) {
          updatesProvince = {
            ...updatesProvince,
            city_id: '',
            city_value: null,
            city_options: [],
            district_id: '',
            district_value: null,
            district_options: [],
            subdistrict_id: '',
            subdistrict_value: null,
            subdistrict_options: [],
          }
        }

        const cityOptions = data?.value ? await getCities(data?.value) : []
        updatesProvince.city_options = cityOptions;
        
        handleChangeAreaAdditionalAddress(updatesProvince, informationIndex)
        break;
      case 'city':
        let updatesCity = {
          city_value: data,
          city_id: data ? data?.value : '',
        };

        if (data?.value != formik?.values?.additional_address[informationIndex]?.city_id) {
          updatesCity = {
            ...updatesCity,
            district_id: '',
            district_value: null,
            district_options: [],
            subdistrict_id: '',
            subdistrict_value: null,
            subdistrict_options: [],
          }
        }

        const districtOptions = data?.value ? await getDistricts(data?.value) : []
        updatesCity.district_options = districtOptions;
    
        handleChangeAreaAdditionalAddress(updatesCity, informationIndex)
        break;
      case 'district':
        let updatesDistrict = {
          district_value: data,
          district_id: data ? data?.value : '',
        };

        if (data?.value != formik?.values?.additional_address[informationIndex]?.district_id) {
          updatesDistrict = {
            ...updatesDistrict,
            subdistrict_id: '',
            subdistrict_value: null,
            subdistrict_options: [],
          }
        }
        
        const subDistrictOptions = data?.value ? await getSubDistricts(data?.value) : []        
        updatesDistrict.subdistrict_options = subDistrictOptions;
    
        handleChangeAreaAdditionalAddress(updatesDistrict, informationIndex)
        break;
      case 'subdistrict':
        const updatesSubDistrict = {
          subdistrict_value: data,
          subdistrict_id: data ? data?.value : '',
          postal_code: data ? data?.postal_code : '',
        };
        
        handleChangeAreaAdditionalAddress(updatesSubDistrict, informationIndex)
        break;
      default:
        break;
    }
  }

  const handleRemoveAdditionalAddress = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      additional_address: values.additional_address.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showConfirmAddAttachments.show && (
          <AppModalConfirmation 
            confirmationType='add-attachments'
            itemName='attachments'
            itemDetails={showConfirmAddAttachments.item}
            showModal={showConfirmAddAttachments}
            setShowModal={setShowConfirmAddAttachments}
            errors={formik.errors?.attachments}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Client Attachments`}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`${showDeleteItem?.item?.file_original_name ? showDeleteItem?.item?.file_original_name : showDeleteItem?.item?.file_name ? showDeleteItem?.item?.file_name : showDeleteItem?.item?.name}`}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveImage}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want create a new client?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Add Client</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            countryOptions={countryOptions}
            provinceOptions={provinceOptions}
            cityOptions={cityOptions}
            districtOptions={districtOptions}
            subDistrictOptions={subDistrictOptions}
            setCountryPhone={setCountryPhone}
            countryPhone={countryPhone}
            countryValue={countryValue}
            provincedValue={provincedValue}
            cityValue={cityValue}
            districtValue={districtValue}
            subDistrictValue={subDistrictValue}
            handleSelectCountryPhone={handleSelectCountryPhone}
            handleSelectCountry={handleSelectCountry}
            handleSelectProvince={handleSelectProvince}
            handleSelectCity={handleSelectCity}
            handleSelectDistrict={handleSelectDistrict}
            handleSelectSubDistrict={handleSelectSubDistrict}
            handleRemoveImage={setShowDeleteItem}
            handleFileInput={handleImageAttachment}
            handleFileOnDrop={handleImagesOnDrop}
            setOpenFilePreview={setOpenFilePreview}
            handleAddOthersPIC={handleAddOthersPIC}
            handleChangeOthersPIC={handleChangeOthersPIC}
            handleSelectCountryOthersPIC={handleSelectCountryOthersPIC}
            handleRemoveOthersPIC={handleRemoveOthersPIC}
            handleAddAdditionalAddress={handleAddAdditionalAddress}
            handleRemoveAdditionalAddress={handleRemoveAdditionalAddress}
            handleChangeAdditionalAddress={handleChangeAdditionalAddress}
            handleSelectAreaAdditionalAddress={handleSelectAreaAdditionalAddress}
          />
        }
        lowerBodyContent={
          <AppCard 
            className='mb-4'
            headerTitle={
              <AppCardHeader
                headerTitle={<h4>Attachments</h4>}
                headerButton="Upload new"
                handleCardHeaderButton={handleCardHeaderButton}
              />
            }
            bodyContent={
              <>
                {
                  formik?.values?.attachments?.length > 0 ? (
                    <FormFileThumbnail 
                      name='attachments'
                      colSize='3'
                      files={formik?.values?.attachments}
                      className='justify-content-start align-items-center'
                      handleConfirmDelete={setShowDeleteItem}
                      handleViewFile={setOpenFilePreview}
                    />
                  )
                  : (
                    <>Not Found</>
                  )
                }
              </>
            }
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/client/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default AddClient